import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { employeeApi } from "../../services/employeeApi";
import { storeApi } from "../../services/storeApi";
import { formatApiErrorDetail } from "../../lib/api";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { StoreSelect } from "../../components/StoreSelect";
import { useLocalization } from "../../hooks/useLocalization";

export default function EmployeeStoreAssignmentReviewPage() {
  const { t } = useLocalization();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);

  const ACTIONS = [
    { value: "ASSIGN_PRIMARY", label: t("mgmtStoreAssignment.actionAssignPrimary"), needsStore: true },
    { value: "ADD_ADDITIONAL", label: t("mgmtStoreAssignment.actionAddAdditional"), needsStore: true },
    { value: "MARK_ORGANIZATION_LEVEL", label: t("mgmtStoreAssignment.actionMarkOrgLevel"), needsStore: false },
    { value: "MARK_INACTIVE", label: t("mgmtStoreAssignment.actionMarkInactive"), needsStore: false },
    { value: "LEAVE_UNRESOLVED", label: t("mgmtStoreAssignment.actionLeaveUnresolved"), needsStore: false },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const res = await employeeApi.listStoreAssignmentReview();
      setEmployees(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("mgmtStoreAssignment.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="store-assignment-review-page">
      <PageHelpBanner pageKey="store-assignment-review" title={t("pageHelp.defaultTitle")}>
        <p>{t("mgmtStoreAssignment.help")}</p>
      </PageHelpBanner>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtStoreAssignment.title")}</h2>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtPayroll.employeeId")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtStoreAssignment.currentStoreText")}</th>
              <th className="px-4 py-3 font-medium text-right">{t("mgmtMedical.colActions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">{t("common.loading")}</td></tr>}
            {!loading && employees.map((e) => (
              <tr key={e.id} data-testid={`store-review-row-${e.id}`}>
                <td className="px-4 py-3 font-medium text-gray-900">{e.legal_name}</td>
                <td className="px-4 py-3 text-gray-500">{e.employee_id}</td>
                <td className="px-4 py-3 text-amber-600 text-xs font-medium">{e.store_location || t("mgmtStoreAssignment.blank")}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`store-review-resolve-btn-${e.id}`} onClick={() => setTarget(e)} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                    {t("mgmtStoreAssignment.review")}
                  </button>
                </td>
              </tr>
            ))}
            {!loading && employees.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500" data-testid="store-review-empty-state">{t("mgmtStoreAssignment.noneReview")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {target && (
        <ReviewDialog employee={target} onClose={() => setTarget(null)} onSaved={() => { setTarget(null); load(); }} actions={ACTIONS} t={t} />
      )}
    </div>
  );
}

function ReviewDialog({ employee, onClose, onSaved, actions, t }) {
  const [action, setAction] = useState("ASSIGN_PRIMARY");
  const [storeName, setStoreName] = useState(null);
  const [storeId, setStoreId] = useState(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);

  useEffect(() => { storeApi.list(false).then((res) => setStores(res.data)); }, []);

  const meta = actions.find((a) => a.value === action);

  const handleStoreChange = (name) => {
    setStoreName(name);
    const match = stores.find((s) => s.name === name);
    setStoreId(match ? match.id : null);
  };

  const handleSave = async () => {
    setError("");
    if (meta.needsStore && !storeId) { setError(t("mgmtStoreAssignment.selectAStore")); return; }
    if (!reason.trim()) { setError(t("mgmtStoreAssignment.reasonRequiredAudit")); return; }
    setSaving(true);
    try {
      await employeeApi.resolveStoreAssignmentReview(employee.id, action, meta.needsStore ? storeId : null, reason);
      toast.success(t("mgmtStoreAssignment.reviewed"));
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="store-review-dialog">
        <DialogHeader><DialogTitle>{t("mgmtStoreAssignment.reviewDialogTitle")} - {employee.legal_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-gray-500">{t("mgmtStoreAssignment.currentStoreTextOnFile")}: <span className="font-semibold">{employee.store_location || t("mgmtStoreAssignment.blank")}</span></p>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAssignment.action")}</label>
            <select data-testid="store-review-action-select" value={action} onChange={(e) => setAction(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm">
              {actions.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </div>
          {meta.needsStore && (
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAssignment.store")}</label>
              <StoreSelect testId="store-review-store-select" value={storeName} onChange={handleStoreChange} />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("mgmtStoreAssignment.reasonRequiredForAudit")}</label>
            <textarea data-testid="store-review-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          </div>
          {error && <p className="text-sm text-red-500" data-testid="store-review-error">{error}</p>}
        </div>
        <DialogFooter>
          <button data-testid="store-review-cancel-btn" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold border border-gray-300">{t("common.cancel")}</button>
          <button data-testid="store-review-save-btn" onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40">
            {saving ? t("mgmtStoreAssignment.savingEllipsis") : t("common.save")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
